import { Observer } from "mobx-react-lite";
import type React from "react";
import { forwardRef, useContext } from "react";
import { useTranslation } from "react-i18next";
import NavigateContext from "../../../stores/contexts/Navigate";
import { type GlobalId, getSavedGlobalId } from "../../../stores/definitions/BaseRecord";
import type { InventoryRecord } from "../../../stores/definitions/InventoryRecord";
import useStores from "../../../stores/use-stores";
import { match } from "../../../util/Util";
import ContextMenuAction, { type ContextMenuRenderOptions } from "./ContextMenuAction";
import RemoveFromBasketIcon from "./RemoveFromBasketIcon";

type RemoveFromBasketActionArgs = {
  as: ContextMenuRenderOptions;
  closeMenu: () => void;
  disabled: string;
  selectedResults: Array<InventoryRecord>;
};

const RemoveFromBasketAction = forwardRef<React.ElementRef<typeof ContextMenuAction>, RemoveFromBasketActionArgs>(
  ({ as, closeMenu, disabled, selectedResults }: RemoveFromBasketActionArgs, ref) => {
    const { t } = useTranslation("inventory");
    const { searchStore } = useStores();
    const { useLocation } = useContext(NavigateContext);
    function useSearchParams() {
      return new URLSearchParams(useLocation().search);
    }
    const searchParams = useSearchParams();

    const itemIds: Array<GlobalId> = selectedResults.map((r) => getSavedGlobalId(r));

    const onRemove = () => {
      const currentBasket = searchStore.savedBaskets.find((b) => b.globalId === searchParams.get("parentGlobalId"));
      if (currentBasket) void currentBasket.removeItems(itemIds);
    };

    const handleOpen = () => {
      onRemove();
      if (typeof closeMenu === "function") closeMenu();
    };

    const disabledHelp = match<void, string>([
      [() => disabled !== "", disabled],
      [() => true, ""],
    ])();

    return (
      <Observer>
        {() => (
          <>
            <ContextMenuAction
              onClick={handleOpen}
              icon={<RemoveFromBasketIcon />}
              label={t("contextMenu.actions.removeFromThisBasket")}
              disabledHelp={disabledHelp}
              as={as}
              ref={ref}
            />
          </>
        )}
      </Observer>
    );
  },
);

RemoveFromBasketAction.displayName = "RemoveFromBasketAction";
export default RemoveFromBasketAction;
